import React from "react";
import { useEffect, useContext } from "react";
import { Button } from "../Button";
import moment from "moment";
import { Input } from "../Input";
import { FiltrosDataContainer } from "./styles";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { ReactComponent as CalenderSVG } from "../../assets/newImages/icons/calender.svg";
import { PedidosContext } from "../../contexts/Pedidos/PedidosContext";

interface valueProps {
  inicio: string;
  fim: string;
  placeholder:
    | "do dia"
    | "do mes"
    | "últimos 30 dias"
    | "intervalo personalizado"
    | string;
}
interface filtrosDataProps {
  onAplicar: (newValue: valueProps) => void;
  value: valueProps;
  hiddenOptions?: string[];
  maxDate?: string;
}

export const FiltrosData: React.FC<filtrosDataProps> = ({
  onAplicar,
  value,
  hiddenOptions = [],
  maxDate,
}) => {
  const { theme } = useTheme();
  const { setFiltros, filtros } = useContext(PedidosContext);
  const isInitializing = React.useRef(true);

  const [dropdown, setDropdown] = React.useState(false);

  const isCustomFilter = React.useMemo(() => {
  return (filtros.placeholder?.toLowerCase() || "") !== "últimos 30 dias";
  }, [filtros.placeholder]);
  
  const [dataInicio, setDataInicio] = React.useState(() => {
    if (filtros.FiltroDataInicio) {
      return moment(filtros.FiltroDataInicio).format("YYYY-MM-DD");
    }
    
    // Try to get from localStorage
    const pedidosSettings = localStorage.getItem("pedidosSettings");
    if (pedidosSettings) {
      const settings = JSON.parse(pedidosSettings);
      if (settings.FiltroDataInicio) {
        return moment(settings.FiltroDataInicio).format("YYYY-MM-DD");
      }
    }
    
    // Default to current date if nothing is found
    return moment().format("YYYY-MM-DD");
  });
  
  const [dataFim, setDataFim] = React.useState(() => {
    if (filtros.FiltroDataFim) {
      return moment(filtros.FiltroDataFim).format("YYYY-MM-DD");
    }
    
    // Try to get from localStorage
    const pedidosSettings = localStorage.getItem("pedidosSettings");
    if (pedidosSettings) {
      const settings = JSON.parse(pedidosSettings);
      if (settings.FiltroDataFim) {
        return moment(settings.FiltroDataFim).format("YYYY-MM-DD");
      }
    }
    
    // Default to current date if nothing is found
    return moment().format("YYYY-MM-DD");
  });
  const values = [
    {
      placeholder: "Do dia",
      value: () => {
        setFiltros({
          ...filtros,
          placeholder: "Do dia",
          FiltroDataInicio: `${moment().format("YYYY-MM-DD")} 00:00:00`,
          FiltroDataFim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
        });
        return {
          inicio: `${moment().format("YYYY-MM-DD")} 00:00:00`,
          fim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
          placeholder: "Do dia",
        };
      },
    },
    {
      placeholder: "Do mês",
      value: () => {
        setFiltros({
          ...filtros,
          placeholder: "Do mês",
          FiltroDataInicio: `${moment().startOf("month").format("YYYY-MM-DD")} 00:00:00`,
          FiltroDataFim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
        });
        return {
          inicio: `${moment().startOf("month").format("YYYY-MM-DD")} 00:00:00`,
          fim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
          placeholder: "Do mês",
        };
      },
    },
    {
      placeholder: "Últimos 30 dias",
      value: () => {
        setFiltros({
          ...filtros,
          placeholder: "Últimos 30 dias",
          FiltroDataInicio: `${moment() .subtract(30, "days").format("YYYY-MM-DD")} 00:00:00`,
          FiltroDataFim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
        });
        return {
          inicio: `${moment()
            .subtract(30, "days")
            .format("YYYY-MM-DD")} 00:00:00`,
          fim: `${moment().format("YYYY-MM-DD")} 23:59:59`,
          placeholder: "Últimos 30 dias",
        };
      },
    },
    {
      placeholder: "Intervalo personalizado",
      
      value: (dataInicio, dataFim) => {
        setFiltros({
          ...filtros,
          placeholder: "Intervalo personalizado",
          FiltroDataInicio: `${dataInicio} 00:00:00`,
          FiltroDataFim: `${dataFim} 23:59:59`,
        });
        return {
          inicio: `${dataInicio} 00:00:00`,
          fim: `${dataFim} 23:59:59`,
          placeholder: "Intervalo personalizado",
        };
      },
    },
  ];
  const [placeholder, setPlaceholder] = React.useState(values[2].placeholder);
  const [selecionado, setSelecionado] = React.useState(values[2]);

  const handleCancelar = () => {
    setDropdown(false);
    setSelecionado(
      values[values.findIndex((value) => value.placeholder === placeholder)]
    );
  };

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDropdown(false);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  // Update effect to also update date inputs when placeholder changes
  useEffect(() => {
    const valueIndex = values.findIndex(
      (value) => value.placeholder === filtros.placeholder
    );
    
    if (valueIndex >= 0) {
      setSelecionado(values[valueIndex]);
      setPlaceholder(filtros.placeholder);
      
      // Update date range based on selected filter type
      if (filtros.placeholder === "Últimos 30 dias") {
        setDataInicio(moment().subtract(30, "days").format("YYYY-MM-DD"));
        setDataFim(moment().format("YYYY-MM-DD"));
      } else if (filtros.placeholder === "Do mês") {
        setDataInicio(moment().startOf("month").format("YYYY-MM-DD"));
        setDataFim(moment().format("YYYY-MM-DD"));
      } else if (filtros.placeholder === "Do dia") {
        setDataInicio(moment().format("YYYY-MM-DD"));
        setDataFim(moment().format("YYYY-MM-DD"));
      }
    }
  }, [filtros]);

  // Initial setup effect to ensure everything is in sync
  useEffect(() => {
    if (isInitializing.current) {
      // For any standard filter type, recalculate dates based on current day
      if (filtros.placeholder === "Últimos 30 dias" || 
          filtros.placeholder === "Do mês" || 
          filtros.placeholder === "Do dia") {
        
        let startDate, endDate;
        const today = moment().format("YYYY-MM-DD");
        
        if (filtros.placeholder === "Últimos 30 dias") {
          startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
          endDate = today;
        } else if (filtros.placeholder === "Do mês") {
          startDate = moment().startOf("month").format("YYYY-MM-DD"); 
          endDate = today;
        } else if (filtros.placeholder === "Do dia") {
          startDate = today;
          endDate = today;
        }
        
        console.log("Setting fresh dates for:", filtros.placeholder, startDate, "to", endDate);
        
        setDataInicio(startDate);
        setDataFim(endDate);
        
        // Always update the context with fresh dates relative to today
        const updatedFiltros = {
          ...filtros,
          FiltroDataInicio: `${startDate} 00:00:00`,
          FiltroDataFim: `${endDate} 23:59:59`,
        };
        
        setFiltros(updatedFiltros);
        
        // Also call onAplicar to ensure the filter is applied right away
        onAplicar({
          inicio: `${startDate} 00:00:00`,
          fim: `${endDate} 23:59:59`,
          placeholder: filtros.placeholder
        });
      }
      
      isInitializing.current = false;
    }
  }, []);

  return (
    <>      <FiltrosDataContainer theme={theme} active={isCustomFilter}>
        <div
          className="filtrosData fonteTexto"
          onClick={() => {
            setDropdown(true);
          }}
        >
          <CalenderSVG />
          <div>{placeholder}</div>
        </div>
        {dropdown && (
          <>
            <div className="fundoDropdown" onClick={handleCancelar}></div>
            <div className="containerDropdown">
              <div className="dropdown">
                <div className="filtros fonteTexto">
                  {values
                    .filter((item) => !hiddenOptions.includes(item.placeholder))
                    .map((item, index) => (
                    <div
                      key={index}
                      className={`filtro ${
                        selecionado.placeholder &&
                        selecionado.placeholder === item.placeholder
                          ? "selecionado"
                          : ""
                      }`}
                      onClick={() => {
                        setSelecionado(item);
                      }}
                    >
                      {item.placeholder}
                    </div>
                  ))}
                </div>
                <div
                  className={`dates-picker ${
                    selecionado.placeholder &&
                    selecionado.placeholder === "Intervalo personalizado" &&
                    "ativo"
                  }`}
                >
                  <label className="fonteTexto">Selecione um intervalo de tempo</label>
                  <div className="datas">
                    <Input
                      type={"date"}
                      label="Data de início"
                      value={dataInicio}
                      placeholder={"01/01/2023"}
                      max={maxDate || "9999-12-31"}
                      onChange={(newValue) => {
                        setDataInicio(newValue);
                        if (moment(newValue).isAfter(moment(dataFim))) {
                          setDataFim(newValue);
                        }
                      }}
                    ></Input>
                    <Input
                      type={"date"}
                      label="Data final"
                      value={dataFim}
                      placeholder={"22/01/2023"}
                      max={maxDate || "9999-12-31"}
                      onChange={(newValue) => {
                        setDataFim(newValue);
                        if (moment(newValue).isBefore(moment(dataInicio))) {
                          setDataInicio(newValue);
                        }
                      }}
                    ></Input>
                  </div>
                </div>
                <div className="aplicarCancelar">
                  <div className="botoes fonteTexto">
                    <Button
                      onClick={() => {
                        setPlaceholder(
                          selecionado.placeholder ? selecionado.placeholder : ""
                        );
                        onAplicar(selecionado.value(dataInicio, dataFim));
                        setDropdown(false);
                      }}
                      title={"Aplicar"}
                    />
                    <Button
                      onClick={() => {
                        const today = moment().format("YYYY-MM-DD");
                        const thirtyDaysAgo = moment().subtract(30, "days").format("YYYY-MM-DD");
                        setDataInicio(thirtyDaysAgo);
                        setDataFim(today);
                        setSelecionado(values[2]);
                        setPlaceholder("Últimos 30 dias");
                        onAplicar(values[2].value(thirtyDaysAgo, today));
                        setDropdown(false);
                      }}
                      title={"Redefinir"}
                    />
                  </div>
                  <div className="cancelar fonteTexto" onClick={handleCancelar}>
                    Cancelar
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </FiltrosDataContainer>
    </>
  );
};
